import { RequireAuth } from '@/components/require-auth'
import {
  AgencyMemberForm,
  AgencyMemberFormValues,
  agencyMemberSchema,
} from '@/components/agency-member'
import { ApiError, Auth } from '@/services/auth-service'
import { getPasswordErrorMessage } from '@rh/shared'
import { UserService } from '@/services/user-service'
import { AgencyService } from '@/services/agency-service'
import { type CustomizeFormValues } from '@/models/customize-form-schemas'
import { mergeAgencyContact } from '@/models/agency-contact'
import { PhoneNumberInput } from '@/components/phone-number-input'
import { LooseUrlInput } from '@/components/loose-url-input'
import { LooseEmailInput } from '@/components/loose-email-input'
import { LinkedInInput } from '@/components/linkedin-input'
import { updateAuthUser, type UserProfile } from '@/stores/auth-store'
import {
  Button,
  Control,
  Notice,
  PasswordInput,
  TextInput,
  useForm,
} from '@tempots/beatui'
import {
  attr,
  dataAttr,
  Ensure,
  Fragment,
  html,
  on,
  prop,
  Signal,
  TNode,
  When,
} from '@tempots/dom'
import { Validation } from '@tempots/std'
import { HTMLTitle, Query } from '@tempots/ui'
import { Track } from '@/services/track'
import { AppLayout } from '@/components/app-layout'
import { AccountSecurity } from '@/components/account-security'
import z from 'zod'

// The contact fields are clearable inputs whose cleared state is null —
// the schema must be nullish (like agencyMemberSchema) or clearing a
// field shows "Invalid input" even though blank is perfectly valid.
const agencySchema = z.object({
  agencyName: z.string().min(1, 'Agency name is required').max(255),
  agencyPhoneNumber: z.string().max(255).nullish(),
  agencyUrl: z.string().nullish(),
  agencyEmail: z.string().nullish(),
  agencyLinkedIn: z.string().nullish(),
})

type AgencyFormValues = z.infer<typeof agencySchema>

// Signup and reset-password trim passwords before storing, so trim here too
// or a current password saved via those flows could never match.
const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string(),
    confirmPassword: z.string(),
  })
  .superRefine((values, ctx) => {
    const passwordError = getPasswordErrorMessage(values.newPassword.trim())
    if (passwordError) {
      ctx.addIssue({
        code: 'custom',
        path: ['newPassword'],
        message: passwordError,
      })
    }
    if (values.newPassword.trim() !== values.confirmPassword.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['confirmPassword'],
        message: 'Passwords do not match.',
      })
    }
  })

type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>

// Shared with the trial page's fields ("Recruiter Phone Number" there) —
// beatui's default label column truncates the longer labels here.
const LABEL_WIDTH = '11.5rem'

type ProfileData = {
  fullName: string
  /** The account identity (better-auth user.email) — sign-in only, never on resumes. */
  loginEmail: string
  agencyName: string
  /** Agency details are template data — owner/admin only, like /customize. */
  canEditAgency: boolean
  /** Owner + admins, shown to members as who to contact about agency data. */
  admins: Array<{ name?: string; email: string; isOwner: boolean }>
  /**
   * The stored legacy customize document. Still load-bearing: its
   * header/footer.agencyDetails are where the `{{agency.*}}` design tokens
   * resolve from, so the Agency section edits it (merged, never replaced —
   * the preferences endpoint overwrites `customize` wholesale).
   */
  customize?: CustomizeFormValues
  recruiter: {
    name: string
    email?: string
    phone?: string
    linkedIn?: string
  }
}

/**
 * The stored value for an agency contact field, honoring its show flag:
 * clearing a field in this form persists show=false with the value left in
 * place (the preferences endpoint deep-merges, so an absent key would keep
 * the old value anyway) — such a field must load back as empty.
 */
function visibleAgencyDetail(
  customize: CustomizeFormValues | undefined,
  valueKey: 'phoneNumber' | 'url' | 'emailAddress' | 'linkedIn',
  showKey: 'showPhoneNumber' | 'showUrl' | 'showEmailAddress' | 'showLinkedIn'
): string | undefined {
  for (const section of [customize?.header, customize?.footer]) {
    const details = section?.agencyDetails
    if (!details) continue
    const value = details[valueKey]
    if (details[showKey] !== false && value?.trim()) return value
  }
  return undefined
}

function optional(value: string | null | undefined): string | undefined {
  return value && value.trim() !== '' ? value.trim() : undefined
}

function Section({
  title,
  description,
  children,
}: {
  title: string
  description: TNode
  children: TNode
}) {
  return html.div(
    attr.class('mb-10'),
    html.h2(attr.class('text-lg font-semibold text-gray-900'), title),
    html.p(attr.class('text-sm text-gray-600 mt-1 mb-4'), description),
    children
  )
}

function SaveRow({ submitting }: { submitting: Signal<boolean> }) {
  return html.div(
    attr.class('flex flex-row gap-4 mt-4'),
    html.div(attr.class('w-[11.5rem]')),
    html.div(
      attr.class('flex-grow'),
      Button(
        {
          type: 'submit',
          color: 'primary',
          loading: submitting,
          disabled: submitting,
        },
        attr.class('w-full'),
        'Save'
      )
    )
  )
}

function messageFromError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message ?? 'Failed to save. Please try again.'
  }
  return 'An unexpected error occurred. Please try again.'
}

export function ProfilePage({ agencyId }: { agencyId: Signal<string> }) {
  return RequireAuth({}, () =>
    Query<string, ProfileData | null>({
      request: agencyId,
      load: async ({ request: currentAgencyId }) => {
        if (!currentAgencyId) return null

        // Load fresh data from server to ensure we have the latest values
        const [agency, userResponse, profileResponse, myRole] =
          await Promise.all([
            AgencyService.getAgency(currentAgencyId),
            UserService.getCurrentUser(),
            UserService.getAgencyProfile(currentAgencyId),
            AgencyService.getMyAgencyRole(currentAgencyId),
          ])

        updateAuthUser(userResponse.user)

        const canEditAgency = AgencyService.canManageTemplate(myRole)

        // Members get a read-only Agency section; tell them who can edit it.
        let admins: ProfileData['admins'] = []
        if (!canEditAgency) {
          const roster = await AgencyService.listMembers(currentAgencyId)
          admins = roster.members
            .filter(m => m.role === 'owner' || m.role === 'admin')
            .map(m => ({
              name: m.fullName || undefined,
              email: m.email,
              isOwner: m.role === 'owner',
            }))
            .sort((a, b) => Number(b.isOwner) - Number(a.isOwner))
        }

        const recruiter = profileResponse.recruiterProfile
        return {
          fullName: userResponse.user.fullName || '',
          loginEmail: userResponse.user.email || '',
          agencyName: agency.name || '',
          canEditAgency,
          admins,
          customize: agency.preferences?.customize,
          recruiter: {
            // Fall back to the account identity for users who skipped
            // onboarding, mirroring the onboarding form's prefill.
            name: recruiter?.name ?? userResponse.user.fullName ?? '',
            email: recruiter?.email ?? userResponse.user.email ?? undefined,
            phone: recruiter?.phone,
            linkedIn: recruiter?.linkedIn,
          },
        }
      },
      success: ({ value: profileData }) => {
        const data = profileData.value

        // --- Your Information: per-agency recruiter contact details -------
        const recruiterSuccess = prop<string | null>(null)
        const recruiterError = prop<string | null>(null)
        const {
          controller: recruiterController,
          submit: submitRecruiter,
          submitting: recruiterSubmitting,
        } = useForm<AgencyMemberFormValues>({
          schema: agencyMemberSchema,
          initialValue: {
            recruiterName: data?.recruiter.name ?? '',
            recruiterEmail: data?.recruiter.email,
            recruiterPhoneNumber: data?.recruiter.phone,
            recruiterLinkedIn: data?.recruiter.linkedIn,
          },
          onSubmit: async formData => {
            try {
              recruiterSuccess.set(null)
              recruiterError.set(null)

              const name = formData.recruiterName.trim()
              const profile: UserProfile = {
                name: name || undefined,
                phone: optional(formData.recruiterPhoneNumber),
                email: optional(formData.recruiterEmail),
                linkedIn: optional(formData.recruiterLinkedIn),
              }

              const response = await UserService.updateAgencyProfile(
                agencyId.value,
                profile
              )

              // Keep the account display name in step, as onboarding does.
              if (name && name !== data?.fullName) {
                const userResponse = await UserService.updateCurrentUser({
                  fullName: name,
                })
                updateAuthUser(userResponse.user)
              } else {
                updateAuthUser(response.user)
              }

              recruiterSuccess.set('Your information has been updated.')
              Track.fieldInput({
                section: 'main',
                field_name: 'recruiter_profile_save',
                previous_value: '',
                new_value: 'success',
              })
              return Validation.valid
            } catch (error) {
              const message = messageFromError(error)
              recruiterError.set(message)
              return Validation.invalid({ message })
            }
          },
        })

        // --- Account: sign-in email + change password ----------------------
        const passwordSuccess = prop<string | null>(null)
        const passwordError = prop<string | null>(null)
        const {
          controller: passwordController,
          submit: submitPassword,
          submitting: passwordSubmitting,
        } = useForm<ChangePasswordFormValues>({
          schema: changePasswordSchema,
          initialValue: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          },
          onSubmit: async formData => {
            try {
              passwordSuccess.set(null)
              passwordError.set(null)

              const message = await Auth.changePassword(
                formData.currentPassword,
                formData.newPassword.trim()
              )

              // Clear the fields without leaving them in an error state:
              // submit marked every field touched, and errors render for
              // touched fields, so blanking alone shows "required" errors
              // right under the success notice.
              for (const fieldName of [
                'currentPassword',
                'newPassword',
                'confirmPassword',
              ] as const) {
                const field = passwordController.field(fieldName)
                field.change('')
                field.resetTouched()
              }

              passwordSuccess.set(message)
              Track.fieldInput({
                section: 'main',
                field_name: 'change_password',
                previous_value: '',
                new_value: 'success',
              })
              return Validation.valid
            } catch (error) {
              const message = messageFromError(error)
              passwordError.set(message)
              return Validation.invalid({ message })
            }
          },
        })

        // --- Agency ---------------------------------------------------------
        const agencySuccess = prop<string | null>(null)
        const agencyError = prop<string | null>(null)
        // Tracks the last-persisted customize doc so a second save in the
        // same session merges into what the server actually holds.
        let currentCustomize = data?.customize
        const {
          controller: agencyController,
          submit: submitAgency,
          submitting: agencySubmitting,
        } = useForm<AgencyFormValues>({
          schema: agencySchema,
          initialValue: {
            agencyName: data?.agencyName || '',
            agencyPhoneNumber: visibleAgencyDetail(
              data?.customize,
              'phoneNumber',
              'showPhoneNumber'
            ),
            agencyUrl: visibleAgencyDetail(data?.customize, 'url', 'showUrl'),
            agencyEmail: visibleAgencyDetail(
              data?.customize,
              'emailAddress',
              'showEmailAddress'
            ),
            agencyLinkedIn: visibleAgencyDetail(
              data?.customize,
              'linkedIn',
              'showLinkedIn'
            ),
          },
          onSubmit: async formData => {
            try {
              agencySuccess.set(null)
              agencyError.set(null)

              await AgencyService.updateAgency(agencyId.value, {
                name: formData.agencyName.trim(),
              })
              // Every field keyed (null = clear): this form owns the whole
              // contact section, unlike the wizard's per-field patches.
              const customize = mergeAgencyContact(currentCustomize, {
                phone: optional(formData.agencyPhoneNumber) ?? null,
                url: optional(formData.agencyUrl) ?? null,
                email: optional(formData.agencyEmail) ?? null,
                linkedIn: optional(formData.agencyLinkedIn) ?? null,
              })
              await AgencyService.updateAgencyPreferences(agencyId.value, {
                customize,
              })
              currentCustomize = customize

              agencySuccess.set('Agency updated.')
              Track.fieldInput({
                section: 'main',
                field_name: 'agency_save',
                previous_value: '',
                new_value: 'success',
              })
              return Validation.valid
            } catch (error) {
              const message = messageFromError(error)
              agencyError.set(message)
              return Validation.invalid({ message })
            }
          },
        })

        const notice = (
          variant: 'success' | 'danger',
          message: Signal<string | null>
        ) =>
          Ensure(message, msg =>
            Notice(
              {
                variant,
                tone: 'prominent',
                title: variant === 'success' ? 'Success' : 'Error',
              },
              attr.class('mb-4'),
              msg
            )
          )

        return AppLayout({
          agencyId,
          content: html.div(
            HTMLTitle('Your Information - ResumeHeaders'),
            attr.class('p-4 sm:p-6 md:p-8 h-full overflow-auto'),
            When(
              agencyId.map(id => id !== null && id !== ''),
              () =>
                html.div(
                  attr.class('max-w-128 mx-auto'),
                  Section({
                    title: 'Your Information',
                    description:
                      'Your contact details for this agency. Designs that use Recruiter smart fields show these values on resumes you brand. They are specific to this agency.',
                    children: html.form(
                      dataAttr('testid', 'recruiter-profile-form'),
                      on.submit(submitRecruiter),
                      attr.class('flex flex-col gap-2'),
                      notice('success', recruiterSuccess),
                      notice('danger', recruiterError),
                      AgencyMemberForm({
                        controller: recruiterController,
                        onFieldChange: (fieldName, previousValue, newValue) =>
                          Track.fieldInput({
                            section: 'main',
                            field_name: fieldName,
                            previous_value: previousValue,
                            new_value: newValue,
                          }),
                      }),
                      SaveRow({ submitting: recruiterSubmitting })
                    ),
                  }),
                  data?.canEditAgency !== true
                    ? Fragment(
                        Section({
                          title: 'Agency',
                          description:
                            "Your agency's details appear on every resume branded here. They are managed by your agency admins.",
                          children: html.dl(
                            attr.class('flex flex-col gap-2 text-sm'),
                            ...[
                              ['Agency Name', data?.agencyName],
                              [
                                'Agency Phone Number',
                                visibleAgencyDetail(
                                  data?.customize,
                                  'phoneNumber',
                                  'showPhoneNumber'
                                ),
                              ],
                              [
                                'Agency Website',
                                visibleAgencyDetail(
                                  data?.customize,
                                  'url',
                                  'showUrl'
                                ),
                              ],
                              [
                                'Agency Email',
                                visibleAgencyDetail(
                                  data?.customize,
                                  'emailAddress',
                                  'showEmailAddress'
                                ),
                              ],
                              [
                                'Agency LinkedIn',
                                visibleAgencyDetail(
                                  data?.customize,
                                  'linkedIn',
                                  'showLinkedIn'
                                ),
                              ],
                            ].map(([label, value]) =>
                              html.div(
                                attr.class('flex gap-2'),
                                html.dt(
                                  attr.class(
                                    'w-[11.5rem] shrink-0 text-gray-500'
                                  ),
                                  label ?? ''
                                ),
                                html.dd(
                                  attr.class('text-gray-800'),
                                  value || '—'
                                )
                              )
                            )
                          ),
                        }),
                        (data?.admins.length ?? 0) > 0
                          ? html.div(
                              attr.class('-mt-4 mb-10 text-sm'),
                              html.div(
                                attr.class('text-gray-600 mb-2'),
                                'Questions about these details? Contact your agency admins:'
                              ),
                              html.ul(
                                attr.class('flex flex-col gap-1'),
                                ...(data?.admins ?? []).map(admin =>
                                  html.li(
                                    attr.class('text-gray-800'),
                                    admin.name || admin.email,
                                    admin.isOwner
                                      ? html.span(
                                          attr.class('text-gray-400'),
                                          ' (owner)'
                                        )
                                      : null,
                                    html.span(
                                      attr.class('text-gray-500'),
                                      ' — ',
                                      html.a(
                                        attr.class(
                                          'text-teal-700 hover:underline'
                                        ),
                                        attr.href(`mailto:${admin.email}`),
                                        admin.email
                                      )
                                    )
                                  )
                                )
                              )
                            )
                          : null
                      )
                    : Section({
                        title: 'Agency',
                        description:
                          "Your agency's details. Designs that use Agency smart fields show these values on resumes anyone at the agency brands; the agency name is also used in PDF filenames.",
                        children: html.form(
                          dataAttr('testid', 'agency-form'),
                          on.submit(submitAgency),
                          attr.class('flex flex-col gap-2'),
                          notice('success', agencySuccess),
                          notice('danger', agencyError),
                          Control(TextInput, {
                            layout: 'horizontal-fixed',
                            labelWidth: LABEL_WIDTH,
                            controller: agencyController.field('agencyName'),
                            label: 'Agency Name',
                            placeholder: 'Acme Recruiting',
                            required: true,
                            onChange: v => {
                              Track.fieldInput({
                                section: 'main',
                                field_name: 'agencyName',
                                previous_value:
                                  agencyController.field('agencyName').signal
                                    .value,
                                new_value: v,
                              })
                            },
                          }),
                          Control(PhoneNumberInput, {
                            layout: 'horizontal-fixed',
                            labelWidth: LABEL_WIDTH,
                            controller:
                              agencyController.field('agencyPhoneNumber'),
                            label: 'Agency Phone Number',
                            onChange: v => {
                              Track.fieldInput({
                                section: 'main',
                                field_name: 'agencyPhoneNumber',
                                previous_value:
                                  agencyController.field('agencyPhoneNumber')
                                    .signal.value ?? '',
                                new_value: v ?? '',
                              })
                            },
                          }),
                          Control(LooseUrlInput, {
                            layout: 'horizontal-fixed',
                            labelWidth: LABEL_WIDTH,
                            controller: agencyController.field('agencyUrl'),
                            label: 'Agency Website',
                            onChange: v => {
                              Track.fieldInput({
                                section: 'main',
                                field_name: 'agencyUrl',
                                previous_value:
                                  agencyController.field('agencyUrl').signal
                                    .value ?? '',
                                new_value: v ?? '',
                              })
                            },
                          }),
                          Control(LooseEmailInput, {
                            layout: 'horizontal-fixed',
                            labelWidth: LABEL_WIDTH,
                            controller: agencyController.field('agencyEmail'),
                            label: 'Agency Email',
                            placeholder: 'info@example.com',
                            onChange: v => {
                              Track.fieldInput({
                                section: 'main',
                                field_name: 'agencyEmail',
                                previous_value:
                                  agencyController.field('agencyEmail').signal
                                    .value ?? '',
                                new_value: v ?? '',
                              })
                            },
                          }),
                          Control(LinkedInInput, {
                            layout: 'horizontal-fixed',
                            labelWidth: LABEL_WIDTH,
                            controller:
                              agencyController.field('agencyLinkedIn'),
                            label: 'Agency LinkedIn',
                            onChange: v => {
                              Track.fieldInput({
                                section: 'main',
                                field_name: 'agencyLinkedIn',
                                previous_value:
                                  agencyController.field('agencyLinkedIn')
                                    .signal.value ?? '',
                                new_value: v ?? '',
                              })
                            },
                          }),
                          SaveRow({ submitting: agencySubmitting })
                        ),
                      }),
                  // --- Account: sign-in credentials -----------------------
                  // Deliberately styled apart from the sections above: those
                  // hold resume-facing data; nothing in this box ever
                  // appears on a resume.
                  html.div(
                    attr.class('mt-16 mb-10 border-t border-gray-200 pt-8'),
                    html.div(
                      attr.class(
                        'rounded-lg border border-gray-200 bg-gray-50 p-4 sm:p-6'
                      ),
                      html.h2(
                        attr.class(
                          'text-sm font-semibold uppercase tracking-wide text-gray-500'
                        ),
                        'Account'
                      ),
                      html.p(
                        attr.class('text-xs text-gray-500 mt-1 mb-4'),
                        'How you sign in to ResumeHeaders. Nothing here appears on resumes: your sign-in email is separate from "Your Email" above, and changing one never changes the other.'
                      ),
                      html.dl(
                        attr.class('flex flex-col gap-2 text-sm mb-5'),
                        html.div(
                          attr.class('flex gap-2'),
                          html.dt(
                            attr.class('w-[11.5rem] shrink-0 text-gray-500'),
                            'Sign-in Email'
                          ),
                          html.dd(
                            attr.class('text-gray-700'),
                            dataAttr('testid', 'login-email'),
                            data?.loginEmail || '—'
                          )
                        )
                      ),
                      html.form(
                        dataAttr('testid', 'change-password-form'),
                        on.submit(submitPassword),
                        attr.class('flex flex-col gap-2'),
                        notice('success', passwordSuccess),
                        notice('danger', passwordError),
                        Control(PasswordInput, {
                          layout: 'horizontal-fixed',
                          labelWidth: LABEL_WIDTH,
                          size: 'sm',
                          controller:
                            passwordController.field('currentPassword'),
                          label: 'Current Password',
                          autocomplete: 'current-password',
                        }),
                        Control(PasswordInput, {
                          layout: 'horizontal-fixed',
                          labelWidth: LABEL_WIDTH,
                          size: 'sm',
                          controller: passwordController.field('newPassword'),
                          label: 'New Password',
                          autocomplete: 'new-password',
                        }),
                        Control(PasswordInput, {
                          layout: 'horizontal-fixed',
                          labelWidth: LABEL_WIDTH,
                          size: 'sm',
                          controller:
                            passwordController.field('confirmPassword'),
                          label: 'Confirm New Password',
                          autocomplete: 'new-password',
                        }),
                        html.div(
                          attr.class('flex flex-row gap-4 mt-3'),
                          html.div(attr.class('w-[11.5rem]')),
                          html.div(
                            attr.class('flex-grow'),
                            Button(
                              {
                                type: 'submit',
                                color: 'primary',
                                size: 'sm',
                                loading: passwordSubmitting,
                                disabled: passwordSubmitting,
                              },
                              'Change Password'
                            )
                          )
                        ),
                        html.div(
                          attr.class('flex flex-row gap-4 mt-1'),
                          html.div(attr.class('w-[11.5rem]')),
                          html.div(
                            attr.class('flex-grow text-xs text-gray-500'),
                            "Don't know your current password? ",
                            html.a(
                              attr.class('text-teal-700 hover:underline'),
                              attr.href('/forgot-password'),
                              'Send yourself a reset link'
                            ),
                            '.'
                          )
                        )
                      ),
                      AccountSecurity()
                    )
                  )
                )
            )
          ),
        })
      },
    })
  )
}
